import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTimes, 
  FaExclamationCircle, 
  FaCheckCircle, 
  FaInfoCircle, 
  FaTicketAlt, 
  FaUser, 
  FaCalendarAlt, 
  FaBuilding, 
  FaClipboardCheck, 
  FaClock,
  FaSave,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import styles from '../CSS/CloseTicketModal.module.css';

const CloseTicketModal = ({ ticket, user, userRole, departamentos, adminsDepartamento, onClose, onCerrar }) => {
  // Estados
  const [resolucion, setResolucion] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  
  // Límite de caracteres para la resolución
  const RESOLUCION_MAX_LENGTH = 300;
  
  // Verificar si el usuario es admin/staff
  const isAdmin = userRole === 'admin' || userRole === 'admin_departamento' || userRole === 'staff';
  
  // Funciones de notificación con URL correcta
  const enviarNotificacionCierreUsuario = async (ticketData, adminEmail, adminNombre, nombreUsuario, resolucionTexto) => {
    try {
      const response = await fetch('https://truebavirtual.onrender.com/api/close/notify-user-closed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ticket: ticketData,
          adminEmail: adminEmail,
          adminNombre: adminNombre,
          nombreUsuario: nombreUsuario,
          resolucionTexto: resolucionTexto
        })
      });

      const result = await response.json();
      return result;
      
    } catch (error) {
      console.error('Error enviando notificación de cierre por usuario:', error);
      return {
        success: false,
        message: 'Error de conexión con el servidor de notificaciones'
      };
    }
  };

  const enviarNotificacionCierreAdmin = async (ticketData, userEmail, userName, adminNombre, resolucionTexto) => {
    try {
      const response = await fetch('https://truebavirtual.onrender.com/api/close/notify-admin-closed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ticket: ticketData,
          userEmail: userEmail,
          userName: userName,
          adminNombre: adminNombre,
          resolucionTexto: resolucionTexto
        })
      });

      const result = await response.json();
      return result;
      
    } catch (error) {
      console.error('Error enviando notificación de cierre por admin:', error);
      return {
        success: false,
        message: 'Error de conexión con el servidor de notificaciones'
      };
    }
  };
  
  // Formatear fecha
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'dd MMMM yyyy, HH:mm', { locale: es });
    } catch (error) {
      return 'Fecha inválida';
    }
  };
  
  // Obtener departamento actual
  const departamento = departamentos?.find(d => d.id === ticket.departamento_id);
  
  // Manejar cambios en el textarea con límite de caracteres
  const handleResolucionChange = (e) => {
    const value = e.target.value;
    const finalValue = value.length > RESOLUCION_MAX_LENGTH 
      ? value.substring(0, RESOLUCION_MAX_LENGTH) 
      : value;
    
    setResolucion(finalValue);
    if (error) setError('');
    if (showConfirmation) setShowConfirmation(false);
  };
  
  // Obtener textos dinámicos según el tipo de usuario
  const getTexts = () => {
    if (isAdmin) {
      return {
        title: 'Cerrar Ticket',
        helpText: 'Proporciona una resolución detallada que explique cómo se ha resuelto el problema. Esta información será visible para el usuario que creó el ticket.',
        labelText: 'Resolución',
        placeholderText: 'Describe detalladamente cómo se ha resuelto este ticket...',
        confirmationTitle: '¿Estás seguro de cerrar este ticket?',
        confirmationText: 'Esta acción marcará el ticket como resuelto. Una vez cerrado, el ticket no podrá ser reabierto.',
        buttonText: 'Cerrar ticket'
      };
    } else {
      return {
        title: 'Cerrar Ticket',
        helpText: 'Si consideras que tu consulta ha sido resuelta o ya no necesitas asistencia, puedes cerrar este ticket. Explica brevemente el motivo del cierre.',
        labelText: 'Motivo del cierre',
        placeholderText: 'Ejemplo: "Mi problema ya fue resuelto", "Ya no necesito asistencia", "Encontré la solución"...',
        confirmationTitle: '¿Estás seguro de cerrar este ticket?',
        confirmationText: 'Una vez cerrado, no podrás añadir más respuestas a este ticket. Si necesitas ayuda adicional, deberás crear un nuevo ticket.',
        buttonText: 'Cerrar ticket'
      };
    }
  };
  
  const texts = getTexts();
  
  // Manejar cerrar ticket con notificaciones por email
  const handleCerrarTicket = async (e) => {
    e.preventDefault();
    
    if (!resolucion.trim()) {
      setError(isAdmin 
        ? 'Debes proporcionar una resolución detallada para cerrar el ticket'
        : 'Debes proporcionar un motivo para cerrar el ticket'
      );
      return;
    }
    
    if (resolucion.trim().length < 10) {
      setError(isAdmin 
        ? 'La resolución debe ser más detallada (mínimo 10 caracteres)'
        : 'El motivo debe ser más detallado (mínimo 10 caracteres)'
      );
      return;
    }
    
    // MODIFICADO: Mostrar confirmación solo la primera vez
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // 1. Cerrar el ticket en la base de datos
      await onCerrar(ticket.id, resolucion);
      
      // 2. Preparar datos del ticket para emails
      const ticketData = {
        ticket_id: ticket.ticket_id || `#${ticket.id.slice(0, 8)}`,
        titulo: ticket.titulo,
        descripcion: ticket.descripcion,
        departamento_nombre: departamento?.nombre || 'Departamento',
        created_by_name: ticket.created_by_name || 'Usuario',
        created_by_email: ticket.created_by_email || '',
        id: ticket.id
      };
      
      // 3. Enviar notificaciones según quien cierre el ticket
      const emailPromises = [];
      
      if (isAdmin) {
        // Admin cierra el ticket → notificar al usuario que lo creó
        const adminNombre = user.displayName || user.name || 'Administrador';
        
        if (ticket.created_by_email) {
          console.log('📧 Enviando notificación al usuario:', ticket.created_by_email);
          emailPromises.push(
            enviarNotificacionCierreAdmin(
              ticketData,
              ticket.created_by_email,
              ticket.created_by_name || 'Usuario',
              adminNombre,
              resolucion
            )
            .then(result => ({
              type: 'user_notification',
              success: result.success,
              message: result.message
            }))
            .catch(error => ({
              type: 'user_notification',
              success: false,
              message: 'Error enviando notificación al usuario'
            }))
          );
        }
        
      } else {
        // Usuario cierra el ticket → notificar al admin asignado o admins del departamento
        const nombreUsuario = user.displayName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Usuario';
        
        // Buscar admin asignado o admins del departamento
        let adminTargets = [];
        
        // Si hay admin asignado específico
        if (ticket.assigned_to_email) {
          adminTargets.push({
            email: ticket.assigned_to_email,
            nombre: ticket.assigned_to_name || 'Administrador'
          });
        } else {
          // Si no hay admin asignado, notificar a todos los admins del departamento
          if (adminsDepartamento && adminsDepartamento.length > 0) {
            const adminsDelDepartamento = adminsDepartamento.filter(
              admin => admin.departamento_id === ticket.departamento_id
            );
            
            adminTargets = adminsDelDepartamento.map(admin => ({
              email: admin.email,
              nombre: admin.nombre || admin.displayName || 'Administrador'
            }));
          }
        }
        
        // Enviar notificación a cada admin objetivo
        adminTargets.forEach(admin => {
          console.log('📧 Enviando notificación al admin:', admin.email);
          emailPromises.push(
            enviarNotificacionCierreUsuario(
              ticketData,
              admin.email,
              admin.nombre,
              nombreUsuario,
              resolucion
            )
            .then(result => ({
              type: 'admin_notification',
              adminEmail: admin.email,
              success: result.success,
              message: result.message
            }))
            .catch(error => ({
              type: 'admin_notification',
              adminEmail: admin.email,
              success: false,
              message: 'Error enviando notificación al administrador'
            }))
          );
        });
      }
      
      // 4. Ejecutar envío de emails (sin esperar a que terminen)
      if (emailPromises.length > 0) {
        Promise.all(emailPromises)
          .then(results => {
            console.log('📧 Resultados de notificaciones de cierre:', results);
            
            results.forEach(result => {
              if (result.success) {
                console.log(`✅ ${result.type}: ${result.message}`);
              } else {
                console.warn(`⚠️ ${result.type}: ${result.message}`);
              }
            });
          })
          .catch(error => {
            console.error('❌ Error general en notificaciones de cierre:', error);
          });
      }
      
      // 5. Cerrar modal inmediatamente
      onClose();
      
    } catch (error) {
      console.error('Error al cerrar ticket:', error);
      setError('Error al cerrar el ticket. Inténtalo de nuevo.');
      setShowConfirmation(false);
      setIsSubmitting(false);
    }
  };
  
  // Animaciones
  const modalVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 30
      } 
    },
    exit: { 
      opacity: 0, 
      y: 20, 
      scale: 0.95,
      transition: { duration: 0.2 } 
    }
  };
  
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
  };
  
  // Animación para panel de confirmación
  const confirmationVariants = {
    hidden: { opacity: 0, y: 10, height: 0 },
    visible: { 
      opacity: 1, 
      y: 0, 
      height: 'auto',
      transition: { 
        duration: 0.3,
        staggerChildren: 0.1
      } 
    },
    exit: { 
      opacity: 0, 
      y: 10, 
      height: 0,
      transition: { duration: 0.2 } 
    }
  };
  
  // Calcular días que el ticket ha estado abierto
  const daysOpen = ticket.created_at ? 
    Math.ceil((new Date() - new Date(ticket.created_at)) / (1000 * 60 * 60 * 24)) : 
    0;
  
  // Obtener color del contador de caracteres
  const getCharCountClass = () => {
    const remaining = RESOLUCION_MAX_LENGTH - resolucion.length;
    if (remaining <= 20) return styles.charDanger;
    if (remaining <= 50) return styles.charWarning;
    return '';
  };
  
  return (
    <motion.div 
      className={styles.modalOverlay}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={overlayVariants}
      onClick={onClose}
    >
      <motion.div 
        className={styles.modalContainer}
        variants={modalVariants}
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <FaClipboardCheck className={styles.titleIcon} />
            <h2 className={styles.modalTitle}>
              {texts.title}
            </h2>
          </div>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {/* Tarjeta de información del ticket - Compacta */}
          <div className={styles.ticketCard}>
            <div className={styles.ticketHeader}>
              <div className={styles.ticketId}>
                <FaTicketAlt className={styles.ticketIcon} />
                {ticket.ticket_id || `#${ticket.id.slice(0, 6)}`}
              </div>
              <div className={`${styles.ticketStatus} ${styles[`status${ticket.estado.charAt(0).toUpperCase() + ticket.estado.slice(1)}`]}`}>
                {ticket.estado.charAt(0).toUpperCase() + ticket.estado.slice(1).replace('_', ' ')}
              </div>
            </div>
            
            <h3 className={styles.ticketTitle}>{ticket.titulo}</h3>
            
            {/* Información básica del ticket */}
            <div className={styles.ticketBasicInfo}>
              <div className={styles.basicInfoRow}>
                <div className={styles.metaItem}>
                  <FaUser className={styles.metaIcon} />
                  <span>{ticket.created_by_name || 'Usuario'}</span>
                </div>
                <div className={styles.metaItem}>
                  <FaBuilding className={styles.metaIcon} />
                  <span>{departamento?.nombre || 'Sin departamento'}</span>
                </div>
                <div className={styles.metaItem}>
                  <FaClock className={styles.metaIcon} />
                  <span>{daysOpen} {daysOpen === 1 ? 'día abierto' : 'días abierto'}</span>
                </div>
              </div>
            </div>
            
            {/* Botón para expandir detalles */}
            <button
              type="button"
              className={styles.toggleDetailsButton}
              onClick={() => setShowTicketDetails(!showTicketDetails)}
            >
              {showTicketDetails ? (
                <>
                  <span>Ocultar detalles</span>
                  <FaChevronUp className={styles.toggleIcon} />
                </>
              ) : (
                <>
                  <span>Ver detalles</span>
                  <FaChevronDown className={styles.toggleIcon} />
                </>
              )}
            </button>
            
            {/* Detalles expandibles */}
            <AnimatePresence>
              {showTicketDetails && (
                <motion.div 
                  className={styles.ticketExpandedDetails}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Fecha de creación:</span>
                    <span className={styles.detailValue}>
                      <FaCalendarAlt className={styles.detailIcon} />
                      {formatDate(ticket.created_at)}
                    </span>
                  </div>
                  
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Descripción original:</span>
                    <div className={styles.ticketDescription}>
                      <p>{ticket.descripcion}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Sección de resolución */}
          <div className={styles.resolutionSection}>
            <h3 className={styles.sectionTitle}>
              <FaCheckCircle className={styles.sectionIcon} />
              {isAdmin ? 'Resolución del ticket' : 'Cierre del ticket'}
            </h3>
            
            <div className={styles.helpText}>
              <FaInfoCircle className={styles.infoIcon} />
              <p>{texts.helpText}</p>
            </div>
            
            <form onSubmit={handleCerrarTicket} className={styles.closeForm}>
              {error && (
                <div className={styles.errorMessage}>
                  <FaExclamationCircle className={styles.errorIcon} />
                  {error}
                </div>
              )}
              
              <div className={styles.formGroup}>
                <label htmlFor="resolucion" className={styles.formLabel}>
                  {texts.labelText}<span className={styles.requiredStar}>*</span>
                </label>
                <textarea
                  id="resolucion"
                  className={styles.formControl}
                  value={resolucion}
                  onChange={handleResolucionChange}
                  placeholder={texts.placeholderText}
                  rows={5}
                  disabled={isSubmitting}
                  required
                  maxLength={RESOLUCION_MAX_LENGTH}
                />
                <div className={`${styles.charCount} ${getCharCountClass()}`}>
                  {resolucion.length}/{RESOLUCION_MAX_LENGTH} caracteres
                  {resolucion.length < 10 && resolucion.length > 0 && (
                    <span className={styles.charWarning}> (Mínimo 10 caracteres)</span>
                  )}
                </div>
              </div>
              
              {/* Panel de confirmación - MODIFICADO: Sin botones duplicados */}
              <AnimatePresence>
                {showConfirmation && (
                  <motion.div 
                    className={styles.confirmationPanel}
                    variants={confirmationVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <div className={styles.confirmationHeader}>
                      <FaExclamationCircle className={styles.confirmationIcon} />
                      <h4>{texts.confirmationTitle}</h4>
                    </div>
                    <p>{texts.confirmationText}</p>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <div className={styles.formFooter}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  <FaTimes className={styles.btnIcon} />
                  <span>Cancelar</span>
                </button>
                
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className={styles.spinner}></span>
                      <span>Cerrando...</span>
                    </>
                  ) : showConfirmation ? (
                    <>
                      <FaCheckCircle className={styles.btnIcon} />
                      <span>Sí, cerrar ticket</span>
                    </>
                  ) : (
                    <>
                      <FaSave className={styles.btnIcon} />
                      <span>{texts.buttonText}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CloseTicketModal;